import type { BillingNpiCheck } from '../types';

export interface NpiCheckNotice {
  severity: 'info' | 'warning';
  title: string;
  message: string;
  /** Not found or not valid: claims under this NPI may be rejected, so do not claim readiness. */
  claimsAtRisk: boolean;
}

/** Plain-language copy for each NPPES outcome that let the sync go ahead (GDN-4339). */
const NOTICES: Partial<Record<BillingNpiCheck['status'], (npi: string) => NpiCheckNotice>> = {
  unverified: npi => ({
    severity: 'info',
    title: 'NPI not checked',
    claimsAtRisk: false,
    message: `We could not reach the national NPI registry (NPPES), so the billing NPI ${npi} was not checked. The identity was sent to Stedi anyway. If the NPI is wrong, contact JotPsych support to change it.`,
  }),
  wrong_type: npi => ({
    severity: 'warning',
    title: 'Check the NPI type',
    claimsAtRisk: false,
    message: `NPPES lists the billing NPI ${npi} as an individual NPI, not an organization (group) NPI. The identity was sent to Stedi. A group practice usually bills under its organization NPI; a solo practice can bill under the provider's own NPI.`,
  }),
  not_found: npi => ({
    severity: 'warning',
    title: 'NPI not found in NPPES',
    claimsAtRisk: true,
    message: `The billing NPI ${npi} is not listed in the national NPI registry (NPPES), or NPPES shows it as deactivated. The identity was sent to Stedi anyway, but claims under this NPI may be rejected. If the NPI is wrong, contact JotPsych support to change it.`,
  }),
  malformed: npi => ({
    severity: 'warning',
    title: 'NPI is not valid',
    claimsAtRisk: true,
    message: `The billing NPI ${npi} is not a valid NPI. An NPI is 10 digits, starts with 1 or 2, and ends in a check digit that must match the rest. The identity was sent to Stedi anyway, but claims under this NPI may be rejected. Contact JotPsych support to change it.`,
  }),
};

/**
 * The notice to show after a successful sync, from the NPPES check the server ran on the
 * billing NPI. Null when nothing needs saying: the NPI checked out, or the identity was
 * already synced and not re-checked (`npi_check` is null).
 */
export function describeNpiCheck(
  npiCheck: BillingNpiCheck | null | undefined,
): NpiCheckNotice | null {
  if (!npiCheck) return null;
  const notice = NOTICES[npiCheck.status];
  return notice ? notice(npiCheck.npi) : null;
}
